/**
 * Share-Kit URL encoder/decoder.
 *
 * Custom presets are shareable via URL query strings:
 *   Drum kit:    https://elpm-studio.com/?kit=eyJsYWJlbCI6...
 *   Bass voice:  https://elpm-studio.com/?bass=eyJsYWJlbCI6...
 *
 * Each payload is `btoa(JSON.stringify({label, basedOn, ..., v:1}))`.
 * Drum kits carry `params` (full DSP schema); bass voices carry `macros`
 * (4-macro overlay on top of a built-in voice).
 *
 * On app load, `consumePendingSharedKit()` reads + clears the kit param,
 * and `consumePendingSharedBass()` reads + clears the bass param. Both
 * are idempotent — refreshing doesn't re-prompt.
 *
 * Why base64 not a backend round trip:
 *   - Zero backend dependency — works in static hosts.
 *   - Payloads are tiny (~250-600 bytes) so URLs stay under 1 KB.
 *   - Producers can paste shareable URLs in Discord / X / DMs without
 *     setting up an account.
 */

const KIT_PARAM  = "kit";
const BASS_PARAM = "bass";
const FX_PARAM   = "fx";
const VERSION = 1;

// ─── Shared base64 helpers ─────────────────────────────────────────────────

function encodeToBase64Url(payload) {
  // btoa requires Latin-1 input — UTF-8 safe via encodeURIComponent then escape.
  const b64 = btoa(unescape(encodeURIComponent(payload)));
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function decodeFromBase64Url(urlSafe) {
  let b64 = urlSafe.replace(/-/g, "+").replace(/_/g, "/");
  while (b64.length % 4) b64 += "=";
  return decodeURIComponent(escape(atob(b64)));
}

function buildUrl(baseUrl, paramName, urlSafe) {
  const u = new URL(baseUrl || window.location.origin);
  u.searchParams.set(paramName, urlSafe);
  return u.toString();
}

function readAndClear(paramName) {
  if (typeof window === "undefined") return null;
  const url = new URL(window.location.href);
  const raw = url.searchParams.get(paramName);
  if (!raw) return null;
  url.searchParams.delete(paramName);
  window.history.replaceState({}, "", url.toString());
  return raw;
}

// ─── Drum kit share ────────────────────────────────────────────────────────

export function encodeKitToUrl({ label, basedOn, params }, baseUrl) {
  const payload = JSON.stringify({ v: VERSION, label, basedOn, params });
  return buildUrl(baseUrl, KIT_PARAM, encodeToBase64Url(payload));
}

export function decodeKitPayload(urlSafe) {
  if (!urlSafe || typeof urlSafe !== "string") return null;
  try {
    const json = decodeFromBase64Url(urlSafe);
    const parsed = JSON.parse(json);
    if (!parsed || parsed.v !== VERSION) return null;
    if (typeof parsed.label !== "string") return null;
    if (typeof parsed.basedOn !== "string") return null;
    if (!parsed.params || typeof parsed.params !== "object") return null;
    return { label: parsed.label, basedOn: parsed.basedOn, params: parsed.params };
  } catch {
    return null;
  }
}

export function consumePendingSharedKit() {
  const raw = readAndClear(KIT_PARAM);
  return raw ? decodeKitPayload(raw) : null;
}

// ─── Bass voice share ──────────────────────────────────────────────────────

export function encodeBassToUrl({ label, basedOn, macros }, baseUrl) {
  const payload = JSON.stringify({ v: VERSION, label, basedOn, macros });
  return buildUrl(baseUrl, BASS_PARAM, encodeToBase64Url(payload));
}

export function decodeBassPayload(urlSafe) {
  if (!urlSafe || typeof urlSafe !== "string") return null;
  try {
    const json = decodeFromBase64Url(urlSafe);
    const parsed = JSON.parse(json);
    if (!parsed || parsed.v !== VERSION) return null;
    if (typeof parsed.label !== "string") return null;
    if (typeof parsed.basedOn !== "string") return null;
    if (!parsed.macros || typeof parsed.macros !== "object") return null;
    return { label: parsed.label, basedOn: parsed.basedOn, macros: parsed.macros };
  } catch {
    return null;
  }
}

export function consumePendingSharedBass() {
  const raw = readAndClear(BASS_PARAM);
  return raw ? decodeBassPayload(raw) : null;
}

// ─── JSFX preset share ─────────────────────────────────────────────────────

export function encodeFxToUrl({ label, source, sliderValues }, baseUrl) {
  const payload = JSON.stringify({ v: VERSION, label, source, sliderValues: sliderValues || {} });
  return buildUrl(baseUrl, FX_PARAM, encodeToBase64Url(payload));
}

export function decodeFxPayload(urlSafe) {
  if (!urlSafe || typeof urlSafe !== "string") return null;
  try {
    const json = decodeFromBase64Url(urlSafe);
    const parsed = JSON.parse(json);
    if (!parsed || parsed.v !== VERSION) return null;
    if (typeof parsed.label !== "string") return null;
    if (typeof parsed.source !== "string") return null;
    return {
      label: parsed.label,
      source: parsed.source,
      sliderValues: (parsed.sliderValues && typeof parsed.sliderValues === "object")
        ? parsed.sliderValues
        : {},
    };
  } catch {
    return null;
  }
}

export function consumePendingSharedFx() {
  const raw = readAndClear(FX_PARAM);
  return raw ? decodeFxPayload(raw) : null;
}
